import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled'
import mapboxgl from 'mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';
import {JRadio, JRadioGroup, JAccordian, JHRule, JToggleInput} from './Components'
import { ExpandLess, ExpandMore } from '@mui/icons-material';

// eslint-disable-next-line import/no-webpack-loader-syntax
(mapboxgl as any).workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// This token is locked to the URL: https://jacobrec.github.io/biketrip-viewer/
mapboxgl.accessToken = 'pk.eyJ1IjoicmVja2hhcmQiLCJhIjoiY2xzdXR6eXc0MXl2MTJrbnc2djgzbHI5bSJ9.4boUfw73i1AnAoRTAdY9uQ';

type DataInfo = {
  locations: string[],
  provinces: [string, number][],
  days: number[],
  lines: number[],
  distances: number[],
  elevations: number[],
  times: number[],
  topspeeds: number[],
  averagespeeds: number[],
  totaldistance: number,
  totaltime: number,
  totalelevation: number,
  startdate: number,
  enddate: number,
}

type PhotoEntry = {
  lat: number;
  lon: number;
  thumbnail: string;
  image: string;
}

const TRIPS_CONFIG = [
  { id: 'bike_across_canada', label: 'Across Canada', dataPrefix: '/bike_data/bike_across_canada/', color: '#fc4c02' },
  { id: 'bike_to_mexico', label: 'To Mexico', dataPrefix: '/bike_data/bike_to_mexico/', color: '#3399ff' },
  { id: 'bike_icefields_parkway', label: 'Icefields Parkway', dataPrefix: '/bike_data/bike_icefields_parkway/', color: '#dff7fa' },
] as const;

function App() {
  const defaultInfo: DataInfo = {locations: [], provinces:[], days:[], lines:[], distances:[], elevations:[], times:[], topspeeds:[], averagespeeds:[], totaltime: 0, totalelevation: 0, totaldistance: 0, startdate: 0, enddate: 0};
  const mapContainer = useRef(null);
  const [map, setMap] = useState<mapboxgl.Map>();
  const [tripData, setTripData] = useState<Record<string, DataInfo>>({});
  const [activeTrip, setActiveTrip] = useState('bike_across_canada');
  const [loaded, setLoaded] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [photosEnabled, setPhotosEnabled] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoEntry | null>(null);
  useEffect(() => {
    if (typeof window === "undefined" || mapContainer.current === null) return;
    if (map) return;

    const mc = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v11',
      center: [-95, 60],
      zoom: 3.0
    });

    mc.on('load', async () => {
      const data: Record<string, DataInfo> = {};
      for (const trip of TRIPS_CONFIG) {
        data[trip.id] = await loadTrip(mc, trip);
      }
      setTripData(data);
      setLoaded(true);
    });
    setMap(mc);
  }, [map]);

  const activeInfo = tripData[activeTrip] || defaultInfo;

  return (
    <div>
      <div id="background">
        <div ref={mapContainer} className="map-container" />
      </div>

      <JHoverBox tripData={tripData} map={map} loaded={loaded} />
      <div className="card" id="panel">
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer"}}
             onClick={() => setPanelMinimized(!panelMinimized)}>
          <h1>Jacob&rsquo;s bike rides</h1>
          {panelMinimized ? <ExpandMore /> : <ExpandLess />}
        </div>
        {!panelMinimized && <div style={{marginLeft: "10px"}}>
          <InlineSummary info={activeInfo} tripLabel={TRIPS_CONFIG.find(t => t.id === activeTrip)?.label} />
          <JTripSelector activeTrip={activeTrip} setActiveTrip={setActiveTrip} />
          <JHRule />
          <JAccordian title="Line Color">
            <JColorbyGroup tripData={tripData} map={map} />
          </JAccordian>
          <JHRule />
          <JAccordian title="Highlights">
            <JHighlightGroup tripData={tripData} map={map} />
          </JAccordian>
          <JHRule />
          <JAccordian title="Photos">
            <JToggleInput enabled={photosEnabled} setEnabled={setPhotosEnabled} label="Show Photos" />
          </JAccordian>
        </div>}
      </div>
      <JPhotoLayer map={map} enabled={photosEnabled} onPhotoClick={setSelectedPhoto} />
      {selectedPhoto && <JPhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />}
    </div>
  )
}

function JTripSelector(props: {activeTrip: string, setActiveTrip: (t: string) => void}) {
  const Wrapper = styled.div`
    display: flex;
    gap: 4px;
  `;
  const TripOption = styled.div<{active: boolean, color: string}>`
    cursor: pointer;
    padding: 4px 10px;
    border: 2px solid ${p => p.active ? p.color : '#ccc'};
    border-radius: 4px;
    font-size: 14px;
    font-weight: ${p => p.active ? 'bold' : 'normal'};
    flex: 1;
    text-align: center;
  `;
  return (
    <div>
      <h3 style={{margin: "4px 0", fontSize: "14px"}}>Trip</h3>
      <Wrapper>
        {TRIPS_CONFIG.map(t => (
          <TripOption key={t.id} active={props.activeTrip === t.id} color={t.color}
                      onClick={() => props.setActiveTrip(t.id)}>
            {t.label}
          </TripOption>
        ))}
      </Wrapper>
    </div>
  )
}

function InlineSummary(props: {info: DataInfo, tripLabel?: string}) {
  const Container = styled.div`
    margin: 8px 0;
    padding: 6px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 13px;
  `;
  const Row = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
  `;
  const Stat = styled.div`
    text-align: center;
    flex: 1;
  `;
  const Val = styled.div`
    font-weight: bold;
    font-size: 14px;
  `;
  const Lbl = styled.div`
    color: #666;
    font-size: 11px;
  `;
  const fmtDate = (ts: number) => {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const tripDays = props.info.startdate ? Math.round((props.info.enddate - props.info.startdate) / 86400) : 0;
  return (
    <Container>
      <Row style={{marginBottom: 4}}>
        <Val style={{fontSize: 13}}>{fmtDate(props.info.startdate)} &mdash; {fmtDate(props.info.enddate)}, {tripDays} days</Val>
      </Row>
      <Row>
        <Stat>
          <Val>{(props.info.totaldistance / 1000).toFixed(1)}km</Val>
          <Lbl>Distance</Lbl>
        </Stat>
        <Stat>
          <Val>{(props.info.totaltime / 3600).toFixed(0)}h</Val>
          <Lbl>Time</Lbl>
        </Stat>
        <Stat>
          <Val>{(props.info.totalelevation / 1000).toFixed(1)}km</Val>
          <Lbl>Ascent</Lbl>
        </Stat>
      </Row>
    </Container>
  )
}

function JHoverBox (props: {tripData: Record<string, DataInfo>, map?: mapboxgl.Map, loaded: boolean}) {
  const [hovered, setHovered] = useState<{tripId: string, segmentIdx: number} | null>(null);
  useEffect(() => {
    for (const [tripId, info] of Object.entries(props.tripData)) {
      if (!info.locations.length) continue;
      for (let i = 0; i < info.lines.length; i++) {
        let id = genMapId(i, info, tripId)
        props.map?.on('mouseenter', id, () => {
          if (props.map?.getPaintProperty(id, 'line-opacity') === 1) {
            setHovered({tripId, segmentIdx: i})
          }
        })
      }
    }
  }, [props.map, props.loaded, props.tripData])
  if (!hovered || !props.tripData[hovered.tripId]) {
    return <div></div>
  }
  const info = props.tripData[hovered.tripId]
  let dayIdx = idxToDayIdx(hovered.segmentIdx, info) - 1
  if (dayIdx < 0 || dayIdx >= info.distances.length) {
    return <div></div>
  }
  let name = info.locations[dayIdx] + " to " + info.locations[dayIdx + 1];
  const tripLabel = TRIPS_CONFIG.find(t => t.id === hovered.tripId)?.label || hovered.tripId;
  const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
  `;
  const Stat = styled.div`
    font-size: 13px;
  `;
  const Label = styled.span`
    color: #666;
  `;
  const Value = styled.span`
    font-weight: bold;
  `;
  return (
    <div className="card" id="hoverbox" style={{position: "absolute", left: "0px", bottom: "0px"}}>
      <h3 style={{margin: "6px"}}> {tripLabel} — Day {dayIdx + 1}: {name} </h3>
      <Grid>
        <Stat><Value>{(info.distances[dayIdx] / 1000).toFixed(1)}km</Value> <Label>Distance</Label></Stat>
        <Stat><Value>{(info.times[dayIdx] / 3600).toFixed(1)}h</Value> <Label>Time</Label></Stat>
        <Stat><Value>{(info.elevations[dayIdx]+0).toFixed(0)}m</Value> <Label>Ascent</Label></Stat>
        <Stat><Value>{(info.topspeeds[dayIdx] * 3.6).toFixed(1)}km/h</Value> <Label>Max Speed</Label></Stat>
        <Stat><Value>{(info.averagespeeds[dayIdx] * 3.6).toFixed(1)}km/h</Value> <Label>Avg Speed</Label></Stat>
      </Grid>
    </div>
  )

}

function JHighlightGroup(props: {tripData: Record<string, DataInfo>, map?: mapboxgl.Map}) {
  const [highlight, setHighlight] = useState("All");
  useEffect(() => {
    const map = props.map;
    if (!map) return;
    for (const [tripId, info] of Object.entries(props.tripData)) {
      if (!info.locations.length) continue;
      const filterDays = (days: number[]) => {
        for (let i = 1; i < info.lines.length; i++) {
          let dayIdx = idxToDayIdx(i, info)
          map?.setPaintProperty(genMapId(i, info, tripId), 'line-opacity', days.includes(dayIdx) ? 1 : 0)
        }
      }
      const topDaysL = (srcList: number[]) => {
        let largest = [[0,0], [0,0], [0,0], [0,0], [0,0]]
        for (let i = 0; i < info.days.length; i++) {
          const l = srcList[i]
          if (largest[0][0] < l) {
            largest[0] = [l, i]
          }
          largest.sort((a, b) => a[0] - b[0])
        }
        return largest.map((a) => a[1]+1)
      }
      const topDays = (p: string) => {
        let srcList = (info as any)[p]
        return topDaysL(srcList)
      }
      const hillyness = (day: number) => info.elevations[day] / (info.distances[day] / 1000)
      const hillinessList = info.distances.map((e, i) => hillyness(i))
      switch (highlight) {
        case 'All': filterDays([...Array(info.days.length+1).keys()]); break;
        case 'Top Distance': filterDays(topDays('distances')); break;
        case 'Top Elevation': filterDays(topDays('elevations')); break;
        case 'Top Time': filterDays(topDays('times')); break;
        case 'Top Hilliness': filterDays(topDaysL(hillinessList)); break;
        case 'Top Average Speed': filterDays(topDays('averagespeeds')); break;
        case 'Top Max Speed': filterDays(topDays('topspeeds')); break;
        default: filterDays([]); break;
      }
    }
  }, [highlight, props.tripData, props.map])
  return (
          <JRadioGroup value={highlight} setter={setHighlight}>
            <JRadio value="All"/>
            <JRadio value="Top Average Speed"/>
            <JRadio value="Top Max Speed"/>
            <JRadio value="Top Distance"/>
            <JRadio value="Top Time"/>
            <JRadio value="Top Elevation"/>
            <JRadio value="Top Hilliness"/>
          </JRadioGroup>
  )
}

function JColorbyGroup(props: {tripData: Record<string, DataInfo>, map?: mapboxgl.Map}) {
  const [colorby, setColorby] = useState("Trip");

  useEffect(() => {
    const map = props.map;
    if (!map) return;
    for (const [tripId, info] of Object.entries(props.tripData)) {
      if (!info.locations.length) continue;

      const tripColor = TRIPS_CONFIG.find(t => t.id === tripId)?.color || '#fc4c02';

      const setLayersToColor = (color: (string|((i: number) => string))) => {
        for (let i = 1; i < info.lines.length; i++) {
          const dayIdx = idxToDayIdx(i, info)
          let id = genMapId(i, info, tripId);
          let c = (typeof color === "string") ? color : color(dayIdx)
          map.setPaintProperty(id, "line-color", c)
        }
      }

      const provinceColorOfDay = (day: number) => {
        let colors = ["#2196f3", "#ff9800", "#43a047", "#81d4fa", "#e53935", "#1a237e", "#00acc1", "#004d40", "#e65100", "#4e342e"]
        let provinces = ["BC", "Alberta", "Saskatchewan", "Manitoba", "Ontario", "Quebec", "New Brunswick", "Nova Scotia", "PEI", "Newfoundland"]
        let points = info.days[day]
        let npi = info.provinces.findIndex((x) => x[1] >= points)
        let p = npi===-1 ? 0 : npi - 1
        const provinceName = info.provinces[p]?.[0]
        const idx = provinces.indexOf(provinceName)
        return idx >= 0 ? colors[idx] : tripColor
      }

      const hillys = info.elevations.map((e, i) => e / info.distances[i])
      const maxDistance = Math.max(...info.distances)
      const minDistance = Math.min(...info.distances)
      const maxElevation = Math.max(...info.elevations)
      const minElevation = Math.min(...info.elevations)
      const maxTop = Math.max(...info.topspeeds)
      const minTop = Math.min(...info.topspeeds)
      const maxAve = Math.max(...info.averagespeeds)
      const minAve = Math.min(...info.averagespeeds)
      const maxHill = Math.max(...hillys)
      const minHill = Math.min(...hillys)

      const colorByDistance = (day: number) =>
        lerpColor("#FFFFFF","#000000", (info.distances[day-1] - minDistance) / (maxDistance - minDistance))
      const colorByElevation = (day: number) =>
        lerpColor("#FFFFFF","#000000", (info.elevations[day-1] - minElevation) / (maxElevation - minElevation))
      const colorBySpeedTop = (day: number) =>
        lerpColor("#FFFFFF","#000000", (info.topspeeds[day-1] - minTop) / (maxTop - minTop))
      const colorBySpeedAve = (day: number) =>
        lerpColor("#FFFFFF","#000000", (info.averagespeeds[day-1] - minAve) / (maxAve - minAve))

      const hillyness = (day: number) => ((hillys[day-1] - minHill) / (maxHill - minHill))
      const colorByHills = (day: number) => {
        return lerpColor("#FFFFFF","#000000", hillyness(day))
      }
      const colorByDay = (day: number) => {
        let colors = ["#2196f3", "#ff9800", "#43a047", "#e53935"]
        return colors[day % colors.length]
      }
      switch (colorby) {
        case "Trip": setLayersToColor(tripColor); break;
        case "Province": setLayersToColor(provinceColorOfDay); break;
        case "Day": setLayersToColor(colorByDay); break;
        case "Elevation": setLayersToColor(colorByElevation); break;
        case "Hilliness": setLayersToColor(colorByHills); break;
        case "Distance": setLayersToColor(colorByDistance); break;
        case "Top Speed": setLayersToColor(colorBySpeedTop); break;
        case "Average Speed": setLayersToColor(colorBySpeedAve); break;
      }
    }
  }, [colorby, props.tripData, props.map])

  return (
          <JRadioGroup value={colorby} setter={setColorby}>
            <JRadio value="Trip"/>
            <JRadio value="Province"/>
            <JRadio value="Day"/>
            <JRadio value="Distance"/>
            <JRadio value="Hilliness"/>
            <JRadio value="Elevation"/>
            <JRadio value="Top Speed"/>
            <JRadio value="Average Speed"/>
          </JRadioGroup>
  )
}

// https://gist.github.com/rosszurowski/67f04465c424a9bc0dae
function lerpColor(a: string, b: string, amount: number) {
    var ah = +a.replace(/#/g, '0x'),
        ar = ah >> 16, ag = (ah >> 8) & 0xff, ab = ah & 0xff,
        bh = +b.replace(/#/g, '0x'),
        br = bh >> 16, bg = (bh >> 8) & 0xff, bb = bh & 0xff,
        rr = ar + amount * (br - ar),
        rg = ag + amount * (bg - ag),
        rb = ab + amount * (bb - ab);
    return '#' + ((1 << 24) + (rr << 16) + (rg << 8) + rb | 0).toString(16).slice(1);
}


async function getDataInfo(prefix: string) {
  const dataUrl = process.env.PUBLIC_URL + prefix + 'info';
  let response = await fetch(dataUrl);
  let jresponse = await response.json();
  return jresponse;
}

async function loadTrip(map: mapboxgl.Map, tripConfig: typeof TRIPS_CONFIG[number]) {
  const info: DataInfo = await getDataInfo(tripConfig.dataPrefix);
  const dataUrl = process.env.PUBLIC_URL + tripConfig.dataPrefix + 'loc';
  let data: number[][] = [];
  let response = await fetch(dataUrl);
  let blob = await response.blob();
  let arr = await blob.arrayBuffer();
  let floats = new Float32Array(arr);

  let lf: number | null = null;
  let idx = 1;
  let c = 0;
  for (let f of floats) {
    if (lf === null) {
      lf = f;
    } else {
      data.push([f, lf]);
      c += 1;
      lf = null;
    }
    if (info.lines[idx] <= c) {
      addLineToMap(data, map, genMapId(idx, info, tripConfig.id), tripConfig.color);
      idx += 1;
      data = [];
    }
  }
  return info;
}

function JPhotoLayer({map, enabled, onPhotoClick}: {map?: mapboxgl.Map, enabled: boolean, onPhotoClick: (p: PhotoEntry) => void}) {
  const [photos, setPhotos] = useState<PhotoEntry[]>([]);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRef = useRef<mapboxgl.Popup>(new mapboxgl.Popup({closeButton: false, closeOnClick: false, offset: 15}));

  useEffect(() => {
    if (!enabled) {
      setPhotos([]);
      return;
    }
    fetch(process.env.PUBLIC_URL + '/bike_data/photos.json')
      .then(r => r.json())
      .then(setPhotos)
      .catch(e => console.error('Failed to load photos:', e));
  }, [enabled]);

  useEffect(() => {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (!enabled || !map || photos.length === 0) return;

    const markers = photos.map(photo => {
      const el = document.createElement('div');
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.borderRadius = '50%';
      el.style.background = '#e53935';
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';
      el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.4)';

      const marker = new mapboxgl.Marker({element: el})
        .setLngLat([photo.lon, photo.lat])
        .addTo(map);

      el.addEventListener('mouseenter', () => {
        popupRef.current
          .setLngLat([photo.lon, photo.lat])
          .setHTML(`<img src="${process.env.PUBLIC_URL}/${photo.thumbnail}" style="width:120px;height:120px;object-fit:cover;border-radius:4px;display:block;">`)
          .addTo(map);
      });

      el.addEventListener('mouseleave', () => {
        popupRef.current.remove();
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onPhotoClick(photo);
      });

      return marker;
    });

    markersRef.current = markers;
    return () => { markers.forEach(m => m.remove()); };
  }, [photos, enabled, map]);

  return null;
}

function JPhotoModal(props: {photo: PhotoEntry, onClose: () => void}) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer'
    }} onClick={props.onClose}>
      <img src={`${process.env.PUBLIC_URL}/${props.photo.image}`} alt=""
           style={{
             maxWidth: '90vw', maxHeight: '90vh',
             objectFit: 'contain', borderRadius: '4px'
           }}
           onClick={e => e.stopPropagation()} />
    </div>
  );
}

function idxToDayIdx (idx: number, info: DataInfo): number {
  let dayIdx = 0
  while (info.lines[idx] > info.days[dayIdx]) {
    dayIdx ++
  }
  return dayIdx
}
function genMapId(idx: number, info: DataInfo, tripId: string): string {
  const dayIdx = idxToDayIdx(idx, info)
  return tripId + "||" + info.locations[dayIdx-1]+ " to " +info.locations[dayIdx] + "||| " + idx
}
function addLineToMap(data: number[][], map: mapboxgl.Map, id: string, color: string) {
  try {
    map.addSource(id, {
      'type': 'geojson',
      'data': {
        'type': 'Feature',
        'properties': {},
        'geometry': {
          'type': 'LineString',
          'coordinates': data
        }
      }
    });

    map.addLayer({
      'id': id,
      'type': 'line',
      'source': id,
      'layout': {
        'line-join': 'round',
        'line-cap': 'round'
      },
      'paint': {
        'line-color': color,
        'line-width': 5
      }
    });
  } catch (e) {
    console.error(id)
    console.error(e, id)
  }
}

export default App;
